import { Fragment, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { css } from '@emotion/react';
import {
  Button,
  Card,
  color,
  FormGroup,
  Icon,
  Input,
  Modal,
  ModalProps,
  spacing,
  toast,
  Typography
} from '@uniquegood/realworld-studio-design';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ATagStyle, CardItemGap, Gap } from '@/styles';
import {
  useModalState,
  usePlanLimits,
  usePostRequest,
  useRequest,
  usePromotionModal
} from '@/hooks';
import { MultilineInputStyle } from '@/components/ActionHandler/styles';
import LoadingSpinner from '@/components/LoadingSpinner';
import { BILLING_KEY_DOMAIN } from '@/constants/pricing';
import { ClickEventName, PromotionTriggerLocation, track } from '@/track';

function Plan() {
  const { modal, openModal, closeModal } = useModalState();
  const { plan } = usePlanLimits();
  const { appId } = useParams<AppParam>();
  const { openPromotionModal } = usePromotionModal();

  const handleOpenPromotionModal = () => {
    openPromotionModal({ triggerLocation: PromotionTriggerLocation.plan_management });
  };

  const { data: { data: applicationPlanInfo } = {}, isValidating: isLoading } = useRequest<
    CommonResponseModel<ApplicationPlanInfoModel>
  >(`/api/applications/${appId}/plans`);

  if (isLoading && !applicationPlanInfo) {
    return <LoadingSpinner />;
  }

  const isUnpaid =
    applicationPlanInfo?.nextBillingAt === undefined || applicationPlanInfo?.nextBillingAt === null;

  return (
    <div css={Gap}>
      <>
        {plan === 'Realworld' && (
          <>
            <BillingPlanSummary plan={applicationPlanInfo?.currentPlan ?? 'Free'} />
            <BillingPaymentHistorySection isUnpaid={isUnpaid} />
          </>
        )}
        {plan === 'Free' && (
          <>
            <BillingPlanSummary
              plan={applicationPlanInfo?.currentPlan ?? 'Free'}
              onOpenPromotionModal={handleOpenPromotionModal}
            />
            <BillingPaymentHistorySection isUnpaid={isUnpaid} />
          </>
        )}
        {plan === 'Pro' && (
          <>
            <BillingPlanSummary
              plan={applicationPlanInfo?.currentPlan ?? 'Free'}
              nextBillingAt={applicationPlanInfo?.nextBillingAt}
              currentPeriodEndAt={applicationPlanInfo?.currentPeriodEndAt}
              planStatus={applicationPlanInfo?.status}
              onOpenPromotionModal={handleOpenPromotionModal}
            />
            <BillingPaymentMethodSection
              paymentMethod={
                applicationPlanInfo?.currentPaymentMethod ?? {
                  cardCompany: '',
                  cardNumber: ''
                }
              }
              isProUnpaid={isUnpaid}
            />
            <BillingPaymentHistorySection isUnpaid={isUnpaid} />
            <PlanCancelSection
              plan={applicationPlanInfo?.currentPlan ?? 'Free'}
              nextBillingAt={applicationPlanInfo?.nextBillingAt}
              closeModal={closeModal}
              openModal={openModal}
              isUnpaid={isUnpaid}
              status={applicationPlanInfo?.status ?? 'Draft'}
            />
          </>
        )}
        {plan !== 'Free' && plan !== 'Pro' && plan !== 'Realworld' && (
          <>
            <BillingPlanSummary
              plan={applicationPlanInfo?.currentPlan ?? 'Free'}
              nextBillingAt={applicationPlanInfo?.nextBillingAt}
              currentPeriodEndAt={applicationPlanInfo?.currentPeriodEndAt}
            />
            <BillingPaymentHistorySection isUnpaid={isUnpaid} />
            <PlanCancelSection
              plan={applicationPlanInfo?.currentPlan ?? 'Free'}
              nextBillingAt={applicationPlanInfo?.nextBillingAt}
              closeModal={closeModal}
              openModal={openModal}
              isUnpaid={isUnpaid}
              status={applicationPlanInfo?.status ?? 'Draft'}
            />
          </>
        )}
      </>
      <Modal {...modal} />
    </div>
  );
}

export default Plan;

interface BillingPlanSummaryProps {
  plan: PlanType;
  nextBillingAt?: string | null;
  currentPeriodEndAt?: string | null;
  planStatus?: ApplicationPlanStatus;
  onOpenPromotionModal?: () => void;
}

const PLAN_NAME_MAP: Record<PlanType, string> = {
  Free: '무료',
  Pro: '프로',
  Enterprise: '엔터프라이즈',
  Class: '클래스',
  Teacher: '티처',
  Realworld: '리얼월드'
};

function BillingPlanSummary({
  plan,
  nextBillingAt = null,
  currentPeriodEndAt = null,
  planStatus,
  onOpenPromotionModal
}: BillingPlanSummaryProps) {
  const handleOpenPromotionModalClick = () => {
    onOpenPromotionModal?.();
  };

  if (plan === 'Realworld') {
    return (
      <Card
        header={{
          content: '사용중인 요금제',
          prefix: <Icon icon="check_circle_solid" size="20px" />
        }}
        cssStyle={CardItemGap}
      >
        <div>
          <Typography as="p" type="body" textColor="initial">
            요금제
          </Typography>
          <Typography
            as="p"
            type="body"
            css={css`
              color: ${color.action_primary_default};
            `}
          >
            {PLAN_NAME_MAP[plan]}
          </Typography>
        </div>
      </Card>
    );
  }

  if (plan === 'Free') {
    return (
      <Card
        header={{
          content: '사용중인 요금제',
          prefix: <Icon icon="check_circle_solid" size="20px" />
        }}
        cssStyle={CardItemGap}
      >
        <div>
          <Typography as="p" type="body" textColor="initial">
            요금제
          </Typography>
          <Typography
            as="p"
            type="body"
            css={css`
              color: ${color.action_primary_default};
            `}
          >
            현재 무료 요금제를 사용중이에요.
          </Typography>
        </div>
        <div>
          <Button
            type="primary"
            cssStyle={css`
              background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
              border: 0;
            `}
            size="large"
            onClick={handleOpenPromotionModalClick}
          >
            요금제 업그레이드
          </Button>
        </div>
      </Card>
    );
  }

  if (plan === 'Pro') {
    if (!nextBillingAt) {
      return (
        <Card
          header={{
            content: '사용중인 요금제',
            prefix: <Icon icon="check_circle_solid" size="20px" />
          }}
          cssStyle={CardItemGap}
        >
          <div>
            <Typography as="p" type="body" textColor="initial">
              요금제
            </Typography>
            <Typography
              as="p"
              type="body"
              css={css`
                color: ${planStatus === 'Active' ? color.action_primary_default : '#D53D40'};
              `}
            >
              {planStatus === 'Active' ? '프로' : '프로 (해지 예정)'}
            </Typography>
          </div>
          <div>
            <Typography as="p" type="body" textColor="initial">
              다음 결제일
            </Typography>
            <Typography
              as="p"
              type="body"
              css={css`
                color: #555555;
              `}
            >
              해당 없음
            </Typography>
          </div>
          <div>
            <Typography as="p" type="body" textColor="initial">
              사용 만료일
            </Typography>
            <Typography
              as="p"
              type="body"
              css={css`
                color: ${color.action_primary_default};
              `}
            >
              {dayjs(currentPeriodEndAt).format('YYYY-MM-DD')}
            </Typography>
            <Typography
              as="p"
              type="body"
              css={css`
                color: #555555;
                white-space: pre-line;
              `}
            >
              {`사용 만료 7일전 안내 메일과 문자가 발송되며, 만료일 이후 채널이 무료 요금제로 전환돼요. \n정책에 따라 만든 콘텐츠가 ‘미공개' 상태로 변경될 수 있어요.`}
            </Typography>
          </div>
          <div>
            <Button
              type="primary"
              cssStyle={css`
                background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
                border: 0;
              `}
              size="large"
              onClick={handleOpenPromotionModalClick}
            >
              요금제 보러가기
            </Button>
          </div>
        </Card>
      );
    }

    return (
      <Card
        header={{
          content: '사용중인 요금제',
          prefix: <Icon icon="check_circle_solid" size="20px" />
        }}
        cssStyle={CardItemGap}
      >
        <div>
          <Typography as="p" type="body" textColor="initial">
            요금제
          </Typography>
          <Typography
            as="p"
            type="body"
            css={css`
              color: ${planStatus === 'Active' ? color.action_primary_default : '#D53D40'};
            `}
          >
            {planStatus === 'Active' ? '프로' : '프로 (해지 예정)'}
          </Typography>
        </div>
        <div>
          <Typography as="p" type="body" textColor="initial">
            {planStatus === 'Active' ? '다음 결제일' : '종료 예정일'}
          </Typography>
          <Typography
            as="p"
            type="body"
            css={css`
              color: ${planStatus === 'Active' ? color.action_primary_default : '#D53D40'};
            `}
          >
            {planStatus === 'Active'
              ? dayjs(nextBillingAt).format('YYYY-MM-DD')
              : dayjs(currentPeriodEndAt).format('YYYY-MM-DD')}
          </Typography>
          <Typography
            as="p"
            type="body"
            css={css`
              color: #555555;
              white-space: pre-line;
            `}
          >
            {planStatus === 'Active'
              ? `자동 결제에 실패하면 안내 메일과 문자가 발송되며, 약 3일 후 채널이 무료 요금제로 전환돼요. \n정책에 따라 만든 콘텐츠가 '미공개' 상태로 변경될 수 있어요.`
              : `종료 예정일까지는 프로 요금제 기능을 정상적으로 이용할 수 있으며, 이후 무료 채널로 전환돼요. \n정책에 따라 만든 콘텐츠가 ‘미공개’ 상태로 변경될 수 있어요.`}
          </Typography>
        </div>
        <div>
          <Button
            type="primary"
            cssStyle={css`
              background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
              border: 0;
            `}
            size="large"
            onClick={handleOpenPromotionModalClick}
          >
            요금제 보러가기
          </Button>
        </div>
      </Card>
    );
  }

  const handleContactClick = () => {
    if (ChannelIO) {
      ChannelIO('showMessenger');
    }
  };

  return (
    <Card
      header={{
        content: '사용중인 요금제',
        prefix: <Icon icon="check_circle_solid" size="20px" />
      }}
      cssStyle={CardItemGap}
    >
      <div>
        <Typography as="p" type="body" textColor="initial">
          요금제
        </Typography>
        <Typography
          as="p"
          type="body"
          css={css`
            color: ${color.action_primary_default};
          `}
        >
          {PLAN_NAME_MAP[plan]}
        </Typography>
      </div>
      <div>
        <Typography as="p" type="body" textColor="initial">
          사용 만료일
        </Typography>
        <Typography
          as="p"
          type="body"
          css={css`
            color: ${color.action_primary_default};
          `}
        >
          {currentPeriodEndAt ? dayjs(currentPeriodEndAt).format('YYYY-MM-DD') : ''}
        </Typography>
        <Typography
          as="p"
          type="body"
          css={css`
            color: #555555;
            white-space: pre-line;
          `}
        >
          {`사용 만료 7일전 안내 메일과 문자가 발송되며, 만료일 이후 채널이 무료 요금제로 전환돼요. \n정책에 따라 만든 콘텐츠가 '미공개' 상태로 변경될 수 있어요.`}
        </Typography>
      </div>
      <div>
        <Button type="basic" size="large" onClick={handleContactClick}>
          1:1 문의하기
        </Button>
      </div>
    </Card>
  );
}

interface BillingPaymentMethodSectionProps {
  paymentMethod: {
    cardCompany: string;
    cardNumber: string;
  };
  isProUnpaid: boolean;
}

function BillingPaymentMethodSection({
  paymentMethod,
  isProUnpaid
}: BillingPaymentMethodSectionProps) {
  const { data: me } = useRequest<MyInfo>('/api/me');
  const { appId } = useParams<AppParam>();
  const { mutate: mutateApplicationPlanInfo } = useRequest<
    CommonResponseModel<ApplicationPlanInfoModel>
  >(`/api/applications/${appId}/plans`);

  const handleChangePaymentMethodClick = () => {
    const accessToken = localStorage.getItem('accessToken');

    window.open(
      `${BILLING_KEY_DOMAIN}/billing?accessToken=${accessToken}&mode=update&customerEmail=${me?.email}&customerName=${me?.name}&applicationId=${appId}&userId=${me?.id}`,
      'billing-popup',
      'width=800,height=800'
    );
  };

  const handleBillingMessage = async (event: MessageEvent) => {
    // 보안: origin 검증 (결제 페이지 도메인만 허용)
    if (event.origin !== BILLING_KEY_DOMAIN) {
      return;
    }

    const { success } = event.data;

    if (success) {
      toast({ message: '결제 수단이 성공적으로 변경됐어요.', type: 'default' });
      await mutateApplicationPlanInfo();
    } else {
      toast({ message: '결제 수단 변경에 실패했어요. 다시 시도해 주세요.', type: 'error' });
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleBillingMessage);

    return () => {
      window.removeEventListener('message', handleBillingMessage);
    };
  }, []);

  if (isProUnpaid) {
    return (
      <Card
        header={{
          content: '결제 수단',
          prefix: <Icon icon="credit_card_solid" size="20px" />
        }}
        cssStyle={CardItemGap}
      >
        <Typography
          as="p"
          type="body"
          css={css`
            color: #8f8f8f;
          `}
        >
          등록된 결제 수단이 없습니다.
        </Typography>
      </Card>
    );
  }

  return (
    <Card
      header={{
        content: '결제 수단',
        prefix: <Icon icon="credit_card_solid" size="20px" />
      }}
      cssStyle={CardItemGap}
    >
      <div>
        <Typography as="p" type="body" textColor="initial">
          {paymentMethod.cardCompany}
        </Typography>
        <Typography
          as="p"
          type="body"
          css={css`
            color: ${color.action_primary_default};
          `}
        >
          {paymentMethod.cardNumber}
        </Typography>
      </div>
      <div>
        <Button
          type="basic"
          size="large"
          cssStyle={css`
            border: 0;
          `}
          onClick={handleChangePaymentMethodClick}
        >
          결제 수단 변경
        </Button>
      </div>
    </Card>
  );
}

interface BillingPaymentHistorySectionProps {
  isUnpaid: boolean;
}

const formatPrice = (amount: number, currency: 'KRW' | 'USD') => {
  switch (currency) {
    case 'KRW':
      return `${new Intl.NumberFormat('ko-KR').format(amount)}원`;
    case 'USD':
      return `$${new Intl.NumberFormat('en-US').format(amount)}`;
    default:
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency
      }).format(amount);
  }
};

const PAYMENT_STATUS_TEXT_MAP: Record<PaymentStatus, string> = {
  Paid: '결제완료',
  Canceled: '결제취소',
  Failed: '결제실패'
};

function BillingPaymentHistorySection({ isUnpaid }: BillingPaymentHistorySectionProps) {
  const { appId } = useParams<AppParam>();
  const [cursor, setCursor] = useState<string | null>(null);
  const [allHistories, setAllHistories] = useState<ApplicationPaymentHistoriesResponseModel[]>([]);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  const { data: { data: paymentHistoriesPaginatedList } = {}, isValidating: isLoading } =
    useRequest<CommonResponseModel<ApplicationPaymentHistoriesResponseModelCursorPaginatedList>>(
      cursor
        ? `/api/applications/${appId}/payments/histories?Cursor=${cursor}&Size=5`
        : `/api/applications/${appId}/payments/histories?Size=5`
    );

  // 초기 데이터 로드 시 allHistories에 설정
  useEffect(() => {
    if (paymentHistoriesPaginatedList && !cursor) {
      const content = paymentHistoriesPaginatedList.content || [];
      setAllHistories(content);
      setHasNext(paymentHistoriesPaginatedList.hasNext || false);
    }
  }, [paymentHistoriesPaginatedList, cursor]);

  // 추가 데이터 로드 시 기존 데이터에 추가
  useEffect(() => {
    if (paymentHistoriesPaginatedList && cursor) {
      const newContent = paymentHistoriesPaginatedList.content || [];
      setAllHistories((prev) => [...prev, ...newContent]);
      setHasNext(paymentHistoriesPaginatedList.hasNext || false);
      setIsLoadingMore(false);
    }
  }, [paymentHistoriesPaginatedList, cursor]);

  const handleLoadMore = () => {
    if (paymentHistoriesPaginatedList?.lastCursor) {
      setIsLoadingMore(true);
      setCursor(paymentHistoriesPaginatedList.lastCursor);
    }
  };

  if (isLoading && !paymentHistoriesPaginatedList) {
    return <LoadingSpinner />;
  }

  if (allHistories.length === 0 && isUnpaid && !isLoading) {
    return (
      <Card
        header={{
          content: '결제 내역',
          prefix: <Icon icon="receipt_solid" size="20px" />
        }}
        css={CardItemGap}
      >
        <Typography
          as="p"
          type="body"
          css={css`
            color: #8f8f8f;
          `}
        >
          결제 내역이 없어요.
        </Typography>
      </Card>
    );
  }

  return (
    <Card
      header={{
        content: '결제 내역',
        prefix: <Icon icon="receipt_solid" size="20px" />
      }}
    >
      {allHistories.length > 0 ? (
        <>
          {allHistories
            .filter((history) => history.status !== 'Failed')
            .map((history, index) => (
              <Fragment key={history.paymentHistoryId || `history-${history.purchasedAt}-${index}`}>
                <FormGroup label="">
                  <Typography as="p" type="body" textColor="initial">
                    {dayjs(history.purchasedAt).format('YYYY-MM-DD')}
                  </Typography>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                    `}
                  >
                    <a
                      href={history.receiptUrl}
                      target="_blank"
                      css={[
                        ATagStyle,
                        css`
                          font-family: Pretendard;
                          font-size: 14px;
                          font-style: normal;
                          font-weight: 400;
                          line-height: 20px;
                          color: ${color.action_primary_default};
                        `
                      ]}
                      rel="noreferrer"
                    >
                      {formatPrice(history.price, history.currency as 'KRW' | 'USD')} /{' '}
                      {history.description}
                    </a>
                    <Typography
                      as="p"
                      type="caption"
                      css={css`
                        color: #adadad;
                        font-weight: 700;
                      `}
                    >
                      {PAYMENT_STATUS_TEXT_MAP[history.status as PaymentStatus]}
                    </Typography>
                  </div>
                </FormGroup>

                {index !== allHistories.length - 1 && (
                  <div
                    css={css`
                      height: 1px;
                      background-color: #f5f5f5;
                      margin: 16px 0;
                    `}
                  />
                )}
              </Fragment>
            ))}
          {hasNext && (
            <Button
              type="basic"
              size="large"
              cssStyle={css`
                margin-top: 24px;
              `}
              onClick={handleLoadMore}
              loading={isLoadingMore}
              disabled={isLoadingMore}
            >
              내역 더보기
            </Button>
          )}
        </>
      ) : (
        <Typography as="p" type="body" textColor="initial">
          결제 내역이 없어요.
        </Typography>
      )}
    </Card>
  );
}

interface PlanCancelSectionProps {
  plan: Exclude<PlanType, 'Free' & 'realworld'>;
  nextBillingAt?: string | null;
  closeModal: () => void;
  openModal: (
    props: Omit<ModalProps, 'open' | 'onClose'> & Partial<Pick<ModalProps, 'onClose'>>
  ) => void;
  isUnpaid: boolean;
  status: ApplicationPlanStatus;
}

const PRO_BENEFITS = [
  { id: 1, content: '월 500 AI 크레딧' },
  { id: 2, content: '콘텐츠 10개 제작' },
  { id: 3, content: '고급 템플릿 사용' },
  { id: 4, content: '모든 상호작용, 실감기술 도구' },
  { id: 5, content: '미션별 완료율 데이터' },
  { id: 6, content: '후원받기 기능' }
];

interface CancelReasonFormData {
  reason: string;
}

interface ProCancelReasonModalProps {
  nextBillingAtDate: string;
  onSubmit: (data: CancelReasonFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function ProCancelReasonModal({
  nextBillingAtDate,
  onSubmit,
  onCancel,
  isLoading
}: ProCancelReasonModalProps) {
  const { handleSubmit, formState, control } = useForm<CancelReasonFormData>({
    mode: 'onChange'
  });

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.common.large};
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        <Controller
          control={control}
          name="reason"
          rules={{ required: true }}
          render={({ field }) => (
            <FormGroup id="reason" label="" requiredIndicator>
              <Input
                {...field}
                multiline
                placeholder="해지 사유를 입력해 주세요."
                cssStyle={MultilineInputStyle}
              />
            </FormGroup>
          )}
        />
        <div>
          <Typography as="span" type="body">
            지금 프로 요금제를 해지해도
          </Typography>
          <Typography
            as="span"
            type="body"
            css={css`
              color: #7800ff;
            `}
          >{` ${nextBillingAtDate}까지는 정상적으로 사용이 가능`}</Typography>
          <Typography
            as="span"
            type="body"
            css={css`
              white-space: pre-line;
            `}
          >
            {`하며, \n그 이후부터 채널이 무료 요금제로 전환됩니다.`}
          </Typography>
        </div>
      </div>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding-top: ${spacing.padding.large};
          padding-left: ${spacing.padding.xlarge2};
          padding-right: ${spacing.padding.xlarge2};
          margin-top: ${spacing.margin.large};
          margin-left: -${spacing.padding.xlarge2};
          margin-right: -${spacing.padding.xlarge2};
          border-top: 1px solid ${color.border_default_subdued};
        `}
      >
        <Button type="basic" size="large" onClick={onCancel}>
          프로 요금제 유지하기
        </Button>
        <Button
          type="destructive"
          size="large"
          disabled={!formState.isValid || isLoading}
          htmlType="submit"
          loading={isLoading}
        >
          구독 해지
        </Button>
      </div>
    </form>
  );
}

function PlanCancelSection({
  plan,
  nextBillingAt,
  closeModal,
  openModal,
  isUnpaid,
  status
}: PlanCancelSectionProps) {
  const { data: me, mutate: mutateMe } = useRequest<MyInfo>('/api/me');
  const { appId } = useParams<AppParam>();
  const { mutate: mutateApp } = useRequest<RealWorldApp>(`/apps/${appId}`);
  const { mutate: mutatePlans } = useRequest<CommonResponseModel<ApplicationPlanInfoModel>>(
    `/api/applications/${appId}/plans`
  );

  const { trigger, isLoading } = usePostRequest<CommonResponseModel<Record<string, never>>>(
    `/api/applications/${appId}/plans/cancel`
  );
  const { data: applicationInfo, mutate: mutateApplicationInfo } = useRequest<
    CommonResponseModel<ApplicationInfoResponseModel>
  >(`/apps/${appId}/info`);

  const handleContactClick = () => {
    if (ChannelIO) {
      ChannelIO('showMessenger');
    }
  };

  const nextBillingAtDate = nextBillingAt
    ? dayjs(nextBillingAt).format('YYYY. M. D. HH시 mm분')
    : '';

  const handleCancelReasonSubmit = async (data: CancelReasonFormData) => {
    if (data.reason) {
      await trigger(data);
      closeModal();
      await mutateApp();
      await mutateMe();
      await mutatePlans();
    }
  };

  const openProCancelReasonModal = async () => {
    let applicationInfoData = applicationInfo;
    let meInfo = me;

    if (!applicationInfo) {
      applicationInfoData = await mutateApplicationInfo();
    }

    if (!meInfo) {
      meInfo = await mutateMe();
    }

    const param = {
      channel_id: applicationInfoData?.data.id,
      channel_name: applicationInfoData?.data.name ?? '',
      plan_type: applicationInfoData?.data.planType,
      use_purpose_type: applicationInfoData?.data.usePurposeType ?? '',
      user_id: meInfo?.id
    };

    openModal({
      title: '해지 사유는 무엇인가요?',
      size: 'medium',
      hasFooter: false,
      isBottomRadius: true,
      children: (
        <ProCancelReasonModal
          nextBillingAtDate={nextBillingAtDate}
          onSubmit={(data: CancelReasonFormData) => {
            handleCancelReasonSubmit(data);
            track.onClick({
              clickEventName: ClickEventName.click_setting_plan_unsubscribe_modal_next_done,
              params: param
            });
          }}
          onCancel={() => {
            track.onClick({
              clickEventName: ClickEventName.click_setting_plan_unsubscribe_modal_next_dismiss,
              params: param
            });
            closeModal();
          }}
          isLoading={isLoading}
        />
      )
    });
  };

  const openProCancelModal = async () => {
    let applicationInfoData = applicationInfo;
    let meInfo = me;

    if (!applicationInfo) {
      applicationInfoData = await mutateApplicationInfo();
    }

    if (!meInfo) {
      meInfo = await mutateMe();
    }

    const param = {
      channel_id: applicationInfoData?.data.id,
      channel_name: applicationInfoData?.data.name ?? '',
      plan_type: applicationInfoData?.data.planType,
      use_purpose_type: applicationInfoData?.data.usePurposeType ?? '',
      user_id: meInfo?.id
    };

    track.onClick({ clickEventName: ClickEventName.click_setting_plan_unsubscribe, params: param });

    openModal({
      title: '프로 요금제 해지 신청',
      size: 'medium',
      children: (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <Typography as="p" type="heading" textColor="initial">
            {`${me ? me.name : ''}님, 정말 프로 요금제를 해지하실건가요?`}
          </Typography>
          <Typography
            as="p"
            type="body"
            textColor="initial"
            css={css`
              white-space: pre-line;
            `}
          >
            {`지금 해지하면 아래 혜택이 모두 사라지며,\n다음 결제일인 ${
              nextBillingAt ? dayjs(nextBillingAt).format('YYYY. M. D') : ''
            }에 채널이 무료 요금제로 전환돼요.`}
          </Typography>
          <div>
            {PRO_BENEFITS.map((benefit) => (
              <div
                key={benefit.id}
                css={css`
                  display: flex;
                  gap: 8px;
                  align-items: center;
                  justify-content: flex-start;
                  padding: 8px;
                `}
              >
                <div>
                  <Icon icon="check_solid" size="20px" color="#7800FF" />
                </div>
                <Typography as="p" type="body" textColor="initial">
                  {benefit.content}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      ),
      primaryAction: {
        content: '혜택 유지하기',
        onAction: () => {
          closeModal();
          track.onClick({
            clickEventName: ClickEventName.click_setting_plan_unsubscribe_modal_dismiss,
            params: param
          });
        }
      },
      secondaryAction: {
        content: '프로 요금제 해지하기',
        onAction: () => {
          openProCancelReasonModal();
          track.onClick({
            clickEventName: ClickEventName.click_setting_plan_unsubscribe_modal_next,
            params: param
          });
        }
      }
    });
  };

  // enterprise, school
  const openPlanCancelModal = async (
    plan: Extract<PlanType, 'Enterprise' | 'Class' | 'Teacher'>
  ) => {
    let applicationInfoData = applicationInfo;
    let meInfo = me;

    if (!applicationInfo) {
      applicationInfoData = await mutateApplicationInfo();
    }

    if (!meInfo) {
      meInfo = await mutateMe();
    }

    const param = {
      channel_id: applicationInfoData?.data.id,
      channel_name: applicationInfoData?.data.name ?? '',
      plan_type: applicationInfoData?.data.planType,
      use_purpose_type: applicationInfoData?.data.usePurposeType ?? '',
      user_id: meInfo?.id
    };

    track.onClick({ clickEventName: ClickEventName.click_setting_plan_cancel, params: param });
    openModal({
      title: `${PLAN_NAME_MAP[plan]} 요금제를 취소할까요?`,
      size: 'medium',
      children: (
        <div>
          <Typography
            as="p"
            type="body"
            textColor="initial"
            css={css`
              white-space: pre-line;
            `}
          >
            {`${PLAN_NAME_MAP[plan]} 요금제는 1:1 문의를 통해 취소할 수 있어요. \n원하는 해지일을 알려주시면 정책에 따라 일정 금액을 환불해 드려요.`}
          </Typography>
        </div>
      ),
      customActionButton: (
        <div
          css={css`
            display: flex;
            gap: 16px;
          `}
        >
          <Button
            type="basic"
            size="large"
            onClick={() => {
              closeModal();
              track.onClick({
                clickEventName: ClickEventName.click_setting_plan_cancel_dismiss,
                params: param
              });
            }}
          >
            취소
          </Button>
          <Button
            type="destructive"
            size="large"
            onClick={() => {
              handleContactClick();
              track.onClick({
                clickEventName: ClickEventName.click_setting_plan_cancel_contact,
                params: param
              });
            }}
          >
            1:1 문의하기
          </Button>
        </div>
      )
    });
  };

  const openUnpaidPlanCancelModal = async () => {
    let applicationInfoData = applicationInfo;
    let meInfo = me;

    if (!applicationInfo) {
      applicationInfoData = await mutateApplicationInfo();
    }

    if (!meInfo) {
      meInfo = await mutateMe();
    }

    const param = {
      channel_id: applicationInfoData?.data.id,
      channel_name: applicationInfoData?.data.name ?? '',
      plan_type: applicationInfoData?.data.planType,
      use_purpose_type: applicationInfoData?.data.usePurposeType ?? '',
      user_id: meInfo?.id
    };

    track.onClick({ clickEventName: ClickEventName.click_setting_plan_cancel, params: param });

    openModal({
      title: '사용중인 요금제를 취소할까요?',
      size: 'small',
      children: (
        <Typography as="p" type="body" textColor="initial">
          1:1 문의하기를 통해 요금제 취소 진행이 가능합니다.
        </Typography>
      ),
      customActionButton: (
        <div
          css={css`
            display: flex;
            gap: 16px;
          `}
        >
          <Button
            type="basic"
            size="large"
            onClick={() => {
              closeModal();
              track.onClick({
                clickEventName: ClickEventName.click_setting_plan_cancel_dismiss,
                params: param
              });
            }}
          >
            취소
          </Button>
          <Button
            type="destructive"
            size="large"
            onClick={() => {
              handleContactClick();
              track.onClick({
                clickEventName: ClickEventName.click_setting_plan_cancel_contact,
                params: param
              });
            }}
          >
            1:1 문의하기
          </Button>
        </div>
      )
    });
  };

  if (isUnpaid) {
    return (
      <div>
        <Button type="plainDestructive" size="medium" onClick={openUnpaidPlanCancelModal}>
          요금제 취소
        </Button>
      </div>
    );
  }

  if (plan !== 'Pro' && plan !== 'Enterprise' && plan !== 'Class' && plan !== 'Teacher') {
    return null;
  }

  return (
    <div>
      {plan === 'Pro' ? (
        status === 'Active' && (
          <Button type="plainDestructive" size="medium" onClick={openProCancelModal}>
            요금제 해지
          </Button>
        )
      ) : (
        <Button type="plainDestructive" size="medium" onClick={() => openPlanCancelModal(plan)}>
          요금제 취소
        </Button>
      )}
    </div>
  );
}
